import css from './views.module.css'
import { Card, ViewHeader, Empty, QueryError, Disclosure } from '../ui'
import { useChangelogEvents, useRuns } from '../data/queries'
import { toChangelog, type ChangelogEntry } from '../data/map/changelog'
import { short } from '../lib/text'
import { useMemo, useState } from 'react'

/**
 * First-person entries the agents wrote about themselves. No table behind this
 * view and none needed: agent.finished carries the agent's own account of the
 * run in payload.result.result.
 *
 * Rendered as text with no layout that assumes a length, because it is model
 * prose and nothing here shapes it.
 */
export function Changelog() {
  const events = useChangelogEvents()
  const runs = useRuns()

  const runInfo = useMemo(() => {
    const map = new Map<string, { agent: string | null; url: string | null }>()
    for (const run of runs.data ?? []) {
      map.set(run.id, { agent: run.agent, url: run.pr_url ?? run.external_url })
    }
    return map
  }, [runs.data])

  if (events.error) return <QueryError error={events.error} />
  const entries = events.data ? toChangelog(events.data, runInfo) : []

  return (
    <>
      <ViewHeader title="Changelog" aside="Written by the agents, about themselves" />
      <Card>
        {entries.length === 0 ? (
          <Empty glyph="[-_-]">
            Nothing written yet. An entry appears here each time an agent finishes a run and says
            what it did.
          </Empty>
        ) : (
          // The newest entry open; the rest close to who wrote it, when, and the
          // first line of what they said.
          entries.map((entry, index) => <Entry key={entry.id} entry={entry} defaultOpen={index === 0} />)
        )}
      </Card>
    </>
  )
}

function Entry({ entry, defaultOpen }: { entry: ChangelogEntry; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={css.logItem}>
      <Disclosure
        open={open}
        onOpenChange={setOpen}
        title={
          <span className={css.logWho}>
            <span className={`${css.logFace} mono`}>{entry.face}</span>
            <span className={`${css.logAgent} mono`}>{entry.agent}</span>
            <span className={css.logDate}>{entry.department} · {entry.date}</span>
          </span>
        }
        // Only while closed: open, the excerpt would be the body's own first
        // line printed twice.
        summary={open ? null : short(entry.body, 160)}
      >
        <div className={css.logBody}>
          {entry.body}
          {entry.url ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <a href={entry.url} target="_blank" rel="noreferrer">What it produced →</a>
            </div>
          ) : null}
        </div>
      </Disclosure>
    </div>
  )
}
